<script setup>
// M26.10 Part 13 — Executive Dashboard. Read-only dashboard over
// Nexceo::Reports::AiCoreCertificationReportService via AiCoreHealthController.
// No aggregation logic here — every number comes straight from the backend
// certification report.
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import aiCoreHealthAPI from 'dashboard/api/nexceo/aiCoreHealth';

const router = useRouter();
const { accountScopedRoute } = useAccount();

const isLoading = ref(false);
const isRegenerating = ref(false);
const error = ref('');
const report = ref(null);
const hasReport = computed(
  () => !!report.value && report.value.status !== 'no_certification_yet'
);

async function load() {
  isLoading.value = true;
  error.value = '';
  try {
    const { data } = await aiCoreHealthAPI.get();
    report.value = data;
  } catch {
    error.value = 'Failed to load AI core certification report.';
  } finally {
    isLoading.value = false;
  }
}

async function regenerate() {
  isRegenerating.value = true;
  error.value = '';
  try {
    const { data } = await aiCoreHealthAPI.regenerate();
    report.value = data;
  } catch {
    error.value = 'Failed to run certification.';
  } finally {
    isRegenerating.value = false;
  }
}

function back() {
  router.push(accountScopedRoute('nexceo_ai_workforce'));
}

function pctColor(value) {
  if (value == null) return '#94a3b8';
  if (value >= 90) return '#10b981';
  if (value >= 60) return '#f59e0b';
  return '#ef4444';
}

function riskColor(value) {
  if (value == null) return '#94a3b8';
  if (value <= 10) return '#10b981';
  if (value <= 40) return '#f59e0b';
  return '#ef4444';
}

const isReady = computed(
  () => report.value?.certification_status === 'READY FOR M27'
);

const METRIC_TILES = computed(() => {
  const r = report.value;
  if (!r) return [];
  const pct = v => (v == null ? '—' : `${v}%`);
  return [
    {
      label: 'Conversation Accuracy',
      value: pct(r.conversation_accuracy_pct),
      color: pctColor(r.conversation_accuracy_pct),
      icon: 'i-lucide-message-circle',
    },
    {
      label: 'Memory Accuracy',
      value: pct(r.memory_accuracy_pct),
      color: pctColor(r.memory_accuracy_pct),
      icon: 'i-lucide-brain',
    },
    {
      label: 'Knowledge Accuracy',
      value: pct(r.knowledge_accuracy_pct),
      color: pctColor(r.knowledge_accuracy_pct),
      icon: 'i-lucide-book-open',
    },
    {
      label: 'Sales Accuracy',
      value: pct(r.sales_accuracy_pct),
      color: pctColor(r.sales_accuracy_pct),
      icon: 'i-lucide-trending-up',
    },
    {
      label: 'Relationship Accuracy',
      value: pct(r.relationship_accuracy_pct),
      color: pctColor(r.relationship_accuracy_pct),
      icon: 'i-lucide-heart-handshake',
    },
    {
      label: 'Workflow Accuracy',
      value: pct(r.workflow_accuracy_pct),
      color: pctColor(r.workflow_accuracy_pct),
      icon: 'i-lucide-workflow',
    },
    {
      label: 'Avg Response Time',
      value:
        r.avg_response_time_ms == null ? '—' : `${r.avg_response_time_ms}ms`,
      color: '#6366f1',
      icon: 'i-lucide-timer',
    },
    {
      label: 'Prompt Size',
      value: r.prompt_size_chars == null ? '—' : `${r.prompt_size_chars} chars`,
      color: '#64748b',
      icon: 'i-lucide-file-text',
    },
    {
      label: 'Hallucination Risk',
      value: pct(r.hallucination_risk_pct),
      color: riskColor(r.hallucination_risk_pct),
      icon: 'i-lucide-alert-triangle',
    },
  ];
});

onMounted(load);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <div
      class="flex items-center gap-3 px-4 h-14 bg-n-background border-b border-n-weak shrink-0"
    >
      <button
        class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
        @click="back"
      >
        <span class="i-lucide-arrow-left size-4" />
      </button>
      <span class="i-lucide-shield-check size-4 text-woot-500 shrink-0" />
      <h1 class="text-base font-semibold text-n-slate-12">
        AI Core Certification
      </h1>

      <div class="ml-auto flex items-center gap-2">
        <button
          :disabled="isRegenerating"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors disabled:opacity-40"
          @click="regenerate"
        >
          <span
            :class="isRegenerating ? 'animate-spin' : ''"
            class="i-lucide-play size-3.5"
          />
          Run Certification
        </button>
        <button
          :disabled="isLoading"
          class="flex items-center justify-center w-8 h-8 rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors disabled:opacity-40"
          title="Refresh"
          @click="load"
        >
          <span
            :class="isLoading ? 'animate-spin' : ''"
            class="i-lucide-refresh-cw size-4"
          />
        </button>
      </div>
    </div>

    <div
      v-if="error"
      class="px-4 py-2 text-sm text-ruby-600 dark:text-ruby-400"
    >
      {{ error }}
    </div>

    <div
      v-if="isLoading && !report"
      class="flex-1 flex items-center justify-center"
    >
      <span class="i-lucide-loader-2 size-7 animate-spin text-woot-500" />
    </div>

    <div
      v-else-if="!hasReport"
      class="flex-1 flex flex-col items-center justify-center gap-3 text-n-slate-9"
    >
      <span class="i-lucide-shield-question size-10" />
      <p class="text-sm">No certification has been run yet.</p>
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-woot-500 text-white hover:bg-woot-600 transition-colors"
        @click="regenerate"
      >
        Run Certification
      </button>
    </div>

    <div v-else class="flex-1 overflow-auto p-5 space-y-5">
      <!-- Verdict banner -->
      <div
        class="flex items-center gap-3 p-4 rounded-xl border"
        :class="
          isReady
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
        "
      >
        <span
          class="size-5"
          :class="
            isReady
              ? 'i-lucide-check-circle-2 text-green-600 dark:text-green-400'
              : 'i-lucide-alert-circle text-amber-600 dark:text-amber-400'
          "
        />
        <div class="min-w-0">
          <p
            class="text-sm font-semibold"
            :class="
              isReady
                ? 'text-green-700 dark:text-green-300'
                : 'text-amber-700 dark:text-amber-300'
            "
          >
            {{ report.certification_status }}
          </p>
          <p class="text-xs text-n-slate-9">
            Score {{ report.overall_score_pct }}% — {{ report.passed }} pass /
            {{ report.warned }} warn / {{ report.failed }} fail
            <template v-if="report.generated_at">
              · generated {{ new Date(report.generated_at).toLocaleString() }}
            </template>
          </p>
        </div>
      </div>

      <!-- Metric tiles -->
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <div
          v-for="tile in METRIC_TILES"
          :key="tile.label"
          class="flex items-center gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <div
            class="flex items-center justify-center w-9 h-9 rounded-xl shrink-0"
            :style="{ backgroundColor: tile.color + '18' }"
          >
            <span
              class="size-4"
              :class="tile.icon"
              :style="{ color: tile.color }"
            />
          </div>
          <div class="min-w-0">
            <p class="text-[11px] text-n-slate-7 leading-tight truncate">
              {{ tile.label }}
            </p>
            <p
              class="text-xl font-bold leading-tight mt-0.5 tabular-nums truncate text-n-slate-12"
            >
              {{ tile.value }}
            </p>
          </div>
        </div>
      </div>

      <!-- Bugs & blockers -->
      <div
        v-if="report.bugs && report.bugs.length"
        class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <h2
          class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-3"
        >
          Bugs & Blockers
        </h2>
        <ul class="flex flex-col gap-1.5">
          <li
            v-for="(bug, idx) in report.bugs"
            :key="idx"
            class="text-xs text-ruby-600 dark:text-ruby-400"
          >
            {{ bug }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
